import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "../lib/api";
import useCanonical from "../lib/useCanonical";
import usePostSeo from "../lib/usePostSeo";
import { categoryPath } from "../lib/categories";
import ArticleCard from "../components/ArticleCard";
import EditorsPick from "../components/EditorsPick";
import Newsletter from "../components/Newsletter";
import ShareButtons from "../components/ShareButtons";
import SocialIcons from "../components/SocialIcons";
import GlossaryLinker from "../components/GlossaryLinker";
import CommentsSection from "../components/CommentsSection";
import SourcesBlock from "../components/SourcesBlock";

export default function PostDetail() {
  const { slug } = useParams();
  useCanonical(`/blog/${slug}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    setLoading(true);
    http
      .get(`/posts/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    http.get(`/posts/${slug}/comments`).then(({ data }) => setComments(data));
    window.scrollTo({ top: 0 });
    // `http` is a stable module import; `setX` setters are stable React refs.
  }, [slug]);

  usePostSeo(data, loading);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        <p className="font-mono text-[11px] tracking-widest uppercase text-[#66726d] animate-pulse">
          steeping…
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        <h2 className="font-serif text-4xl">Post not found.</h2>
        <Link to="/blog" className="inline-block mt-6 btn-secondary">
          Back to the Journal
        </Link>
      </div>
    );
  }

  const { post, related } = data;
  const dateStr = new Date(post.created_at).toLocaleDateString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="relative z-10" data-testid="post-detail-page">
      {/* HERO */}
      <article>
        <header className="max-w-4xl mx-auto px-6 md:px-12 pt-10 md:pt-14 text-center">
          <Link
            to={categoryPath(post.category)}
            className="overline hover:text-[#10301f]"
          >
            {post.category}
          </Link>
          <h1 className="mt-6 headline-xl text-4xl md:text-6xl lg:text-7xl">
            {post.title}
          </h1>
          <p className="mt-8 text-lg md:text-xl font-light italic text-[#404e48] max-w-2xl mx-auto">
            {post.excerpt}
          </p>
          <div className="mt-10 flex items-center justify-center gap-4 text-sm text-[#66726d]">
            <div className="text-center">
              <p className="font-medium text-[#151e1a]">{post.author_name}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest">
                <time dateTime={post.created_at}>{dateStr}</time> ·{" "}
                {post.read_time} min read
              </p>
            </div>
          </div>
        </header>

        <div className="mt-14 md:mt-20 max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="aspect-[16/9] overflow-hidden rounded-card bg-[#f1eadb]">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-6 md:px-8 py-10 md:py-14 prose-article">
          <GlossaryLinker text={post.content} postSlug={post.slug} />
        </div>

        <SourcesBlock sources={post.sources} />

        <div
          className="max-w-2xl mx-auto px-6 md:px-8 pb-8 border-b"
          style={{ borderColor: "var(--border)" }}
        >
          <ShareButtons
            title={post.title}
            slug={post.slug}
            url={`${typeof window !== "undefined" ? window.location.origin : ""}/blog/${post.slug}`}
          />
        </div>

        {post.tags?.length > 0 && (
          <div className="max-w-2xl mx-auto px-6 md:px-8 pt-8 pb-10 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span
                key={t}
                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#5a1f24]/30 text-[#5a1f24] rounded-full"
              >
                #{t}
              </span>
            ))}
          </div>
        )}

        <div className="max-w-2xl mx-auto px-6 md:px-8 pb-16 flex flex-col items-start gap-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
            Follow The Tea List
          </p>
          <SocialIcons size="md" showHandles />
        </div>
      </article>

      <CommentsSection
        slug={post.slug}
        comments={comments}
        onPosted={(c) => setComments((prev) => [c, ...prev])}
      />

      {/* EDITOR'S PICK — read-time-aware recommendations */}
      <EditorsPick slug={post.slug} />

      {/* RELATED FROM SAME CATEGORY */}
      {related?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-16">
          <p className="overline mb-3">Keep reading</p>
          <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight mb-12">
            More from <span className="italic">{post.category}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {related.map((p) => (
              <ArticleCard key={p.slug} post={p} />
            ))}
          </div>
        </section>
      )}

      <Newsletter />
    </div>
  );
}
